import { useContext, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import { queuesState } from '../atoms/queues-state';
import { userProfileState } from '../atoms/user-profile-state';
import { WSMessages } from '../web-socket/messages';
import { WSContext } from '../ws-context/ws-context';
import { SocketClient } from '../web-socket/socket-client';
import { type QueueModel } from '../types/models/queue-model';

export const PageLayout = styled.section`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
`;

export const MainContentLayout = styled.section`
  display: grid;
`;

export function AppLayout() {
  const setQueuesState = useSetRecoilState(queuesState);
  const queues = useRecoilValue(queuesState);
  const setUserProfileState = useSetRecoilState(userProfileState);
  const { sendWSMessage, socketIsOpen, subscribeMessage, unsubscribeMessage } =
    useContext(WSContext);

  useEffect(() => {
    console.log('useEffect getMyQueues', socketIsOpen);
    if (!socketIsOpen) {
      return;
    }
    sendWSMessage(WSMessages.getMyQueues());
  }, [sendWSMessage, socketIsOpen]);

  /**
   * The server says a followed queue changed (a ticket called, someone left),
   * or the app came back from the background. Refetching the customer's queues
   * refreshes the detail page too, since it renders from the same state. A
   * booth call fires several changes at once, hence the short debounce.
   */
  useEffect(() => {
    if (!socketIsOpen) {
      return;
    }
    const client = SocketClient.getInstance();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        sendWSMessage(WSMessages.getMyQueues());
      }, 300);
    };
    client.on('qmob/queue-changed', refresh);
    client.on('resume', refresh);
    return () => {
      if (timer) clearTimeout(timer);
      client.off('qmob/queue-changed', refresh);
      client.off('resume', refresh);
    };
  }, [sendWSMessage, socketIsOpen]);

  useEffect(() => {
    if (!socketIsOpen) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subscribeMessage('qmob/get-my-queues-response', (msg: any) => {
      setQueuesState(msg.queues);
      setUserProfileState(msg.customer);
    });
    return () => {
      unsubscribeMessage('qmob/get-my-queues-response');
    };
  }, [
    sendWSMessage,
    setQueuesState,
    setUserProfileState,
    socketIsOpen,
    subscribeMessage,
    unsubscribeMessage
  ]);

  useEffect(() => {
    if (!socketIsOpen) {
      return;
    }
    subscribeMessage(
      'qmob/get-queue-detail-response',
      (msg: { queue: QueueModel }) => {
        const inputQueue = msg.queue;
        const idx = queues.findIndex((q) => q.id === inputQueue.id);
        if (idx >= 0) {
          const newState = queues.map((q, i) => (i === idx ? inputQueue : q));
          setQueuesState(newState);
        }
      }
    );
    return () => {
      unsubscribeMessage('qmob/get-queue-detail-response');
    };
  }, [
    queues,
    sendWSMessage,
    setQueuesState,
    setUserProfileState,
    socketIsOpen,
    subscribeMessage,
    unsubscribeMessage
  ]);

  return <Outlet />;
}
